import './TaxesPage.scss';

import { bool, object } from 'prop-types';
import React, { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Store from 'state/Store';

import { ReactComponent as PrintIcon } from 'assets/icons/print.svg';

import { PageHeader } from 'components/pages/Header/PageHeader';
import IRASummaryV2 from 'components/layouts/Taxes/IRASummaryV2';
import TaxResourcesV2 from 'components/layouts/Taxes/TaxResourcesV2';
import TaxSummaryV2 from 'components/layouts/Taxes/TaxSummaryV2';
import YearToDateTableV2 from 'components/layouts/Taxes/YearToDateTableV2';
import LegalCopy from 'components/shared/LegalCopy/LegalCopy';
import Content from 'services/Content';
import {
  taxSummaryEnabled,
  residentialAddressEnabled,
} from 'services/FeatureFlags';
import {
  getTaxes,
  getContributionPeriod,
  getContributionLimit,
  getCustomerMailingAddress,
  getCustomerResidentialAddress,
  getCDSPortfolio,
  getUnrealizedGainsLosses,
  getRealizedGainsLossesCurrent,
  getRealizedGainsLossesPrior
} from 'services/Rest';
import {
  hasReceivedMailingAddress,
  hasReceivedResidentialAddress
} from 'services/IRAContributionCard';
import { scrollPage } from 'support/LayoutUtils';
import {
  getCustomerResidentialState,
  isRetirementAdvisoryServiceClient as isRetirementAdvisoryServiceClientSelector
} from 'state/customer/CustomerSelectors';
import {
  hasBrokerageOrManagedAccounts,
  hasOwnedBrokerage,
  hasOwnedRetirementAccounts as hasOwnedRetirementAccountsSelector,
  getPreScrollFormatMobile
} from 'state/account/AccountSelectors';
import { setIsPrintMode } from 'state/Actions';
import { REQUEST_STATES } from 'state/requests/RequestStatusReducer';
import {
  getHasErrorTaxonomy,
  getHasRealizedCurrentYearDataError,
  getHasRealizedPriorYearDataError,
  getHasReceivedRealizedCurrentYearData,
  getHasReceivedRealizedPriorYearData,
  getHasReceivedTaxonomy,
  getHasReceivedUnrealizedData,
  getHasUnrealizedDataError,
  getRequestStatus,
  hasFailedRequestingTaxes,
  hasReceivedTaxes
} from 'state/requests/RequestStatusSelectors';
import { getRouter } from 'state/router/RouterSelectors';
import {
  getCostBasisTableData,
  getIRASummary,
  getTaxFilter,
  getTaxSummaryV2,
  getTaxesTaxonomyData,
  getUnrealizedTableData,
  getRealizedTableData,
  getCostBasisAsOfDateV2
} from 'state/taxes/TaxSelectors';
import { isDesktopLayout, isMobileLayout } from 'state/view/ViewSelectors';
import {
  getTaxonomyDataV2,
  getPershingAccounts,
  hasOnlyViewOnlyPerishingAccounts as hasOnlyViewOnlyPerishingAccountsSelector
} from 'state/taxonomy/TaxonomySelectors';
import CostBasisTableV2 from 'components/layouts/Taxes/CostBasisTableV2';
import PageLoadingSpinner from 'components/layouts/Navigation/PageLoadingSpinner';
import { BrokerageLinkWithPermissions } from './TaxesPageUtility';
import CallToAction from 'components/layouts/CallToAction/CallToAction';
import TestAndTarget from 'components/shared/TestAndTarget/TestAndTarget';
import { FormattedDate } from 'components/shared/Text/Text';
import { AAMBeaconButton } from 'components/shared/Beacon';

const TaxesMbox = 'ret-aa-primary-page-alert';
const dataAtProperty = 'eb143c91-3999-92fd-60bc-2dca0b84690f';
const CTATarget = TestAndTarget(CallToAction, TaxesMbox, dataAtProperty);

const TaxesPage = (props) => {
  const [printYear, setPrintYear] = useState();

  useEffect(() => {
    getCDSPortfolio();
  }, []);
  useEffect(() => {
    const { hasBrokerageOrManagedAccount, requestStatus, router } = props;
    if (residentialAddressEnabled()) {
      if (!hasReceivedResidentialAddress(requestStatus)) {
        getCustomerResidentialAddress();
      }
    } else {
      if (!hasReceivedMailingAddress(requestStatus)) {
        getCustomerMailingAddress();
      }
    }

    const taxesNotReceived = requestStatus.taxes !== REQUEST_STATES.received;
    if (taxesNotReceived && !requestStatus.taxes) {
      getTaxes();
    }

    const unrealizedNotReceived =
      requestStatus.unrealizedGainsLosses !== REQUEST_STATES.received;
    if (
      unrealizedNotReceived &&
      !requestStatus.unrealizedGainsLosses &&
      hasBrokerageOrManagedAccount
    ) {
      getUnrealizedGainsLosses();
    }

    const realizedPriorYearNotReceived =
      requestStatus.realizedGainsLossesPrior !== REQUEST_STATES.received;
    if (
      realizedPriorYearNotReceived &&
      !requestStatus.realizedGainsLossesPrior &&
      hasBrokerageOrManagedAccount
    ) {
      getRealizedGainsLossesPrior();
    }

    const realizedCurrentYearNotReceived =
      requestStatus.realizedGainsLossesCurrent !== REQUEST_STATES.received;
    if (
      realizedCurrentYearNotReceived &&
      !requestStatus.realizedGainsLossesCurrent &&
      hasBrokerageOrManagedAccount
    ) {
      getRealizedGainsLossesCurrent();
    }

    const hash = router.location.hash;
    if (hash && hash === '#cost-basis-table') {
      const id = document.getElementById('cost-basis-table');
      setTimeout(function () {
        window.scrollTo(0, scrollPage(id, 148)); // offset 48 for margin, 100 to center table
      }, 1);
    }
  }, [props.hasBrokerageOrManagedAccount]);

  useEffect(() => {
    const x = document.querySelectorAll('[data-prescroll]');
    const tableId = x[0]; // find the table with the row to scroll to

    if (preScrollFormatMobile && tableId) {
      const accountIdElt = document.getElementById(
        `${preScrollFormatMobile.taxesUniqueId}-${preScrollFormatMobile.tableFormat}`
      ); // select the row specified with anchor url with id

      setTimeout(function () { // scroll the row into view
        if (accountIdElt) {
          accountIdElt.scrollIntoView();
        }
      }, 1);
    }
  }, [props]);

  useEffect(() => {
    const { requestStatus, customerResidentialState } = props;
    const contributionLimitNotReceived =
      requestStatus.contributionLimit !== REQUEST_STATES.received;
    const contributionPeriodNotReceived =
      requestStatus.contributionPeriod !== REQUEST_STATES.received;

    if (residentialAddressEnabled()) {
      if (
        hasReceivedResidentialAddress(requestStatus) &&
        !requestStatus.contributionLimit &&
        contributionLimitNotReceived
      ) {
        getContributionLimit();
      }

      if (
        hasReceivedResidentialAddress(requestStatus) &&
        !requestStatus.contributionPeriod &&
        contributionPeriodNotReceived
      ) {
        getContributionPeriod(
          customerResidentialState ? customerResidentialState : 'MD'
        );
      }
    }

    if (
      hasReceivedMailingAddress(requestStatus) &&
      !requestStatus.contributionLimit &&
      contributionLimitNotReceived
    ) {
      getContributionLimit();
    }

    if (
      hasReceivedMailingAddress(requestStatus) &&
      !requestStatus.contributionPeriod &&
      contributionPeriodNotReceived
    ) {
      getContributionPeriod(
        customerResidentialState ? customerResidentialState : 'MD'
      );
    }
  }, [props]);

  const handlePrint = () => {
    Store.dispatch(setIsPrintMode(true));
    setTimeout(() => {
      window.print();
      Store.dispatch(setIsPrintMode(false));
    }, 2000);
  };

  const {
    costBasisTableData,
    ownsBrokerage,
    iraSummary,
    isDesktopLayout,
    isMobileLayout,
    taxFilter,
    taxSummary,
    taxonomyData,
    taxonomyDataLayout,
    pershingAccounts,
    hasTaxonomyDataError,
    hasTaxonomyData,
    failedTaxes,
    receivedTaxes,
    unrealizedTableData,
    realizedTableData,
    hasReceivedUnrealizedData,
    hasUnrealizedDataError,
    hasReceivedRealizedPriorData,
    hasRealizedPriorDataError,
    hasReceivedRealizedCurrentData,
    hasRealizedCurrentDataError,
    hasBrokerageOrManagedAccount,
    hasOwnedRetirementAccounts,
    isRetirementAdvisoryServiceClient,
    hasOnlyViewOnlyPerishingAccounts,
    router,
    asOfDate,
    preScrollFormatMobile,
  } = props;

  const hasTaxAndTaxonomySuccessOrFail =
    (hasTaxonomyData || hasTaxonomyDataError) && (failedTaxes || receivedTaxes);
  const hasRZAndRZSuccessOrFail =
    (hasReceivedUnrealizedData || hasUnrealizedDataError) &&
    (hasReceivedRealizedPriorData || hasRealizedPriorDataError) &&
    (hasReceivedRealizedCurrentData || hasRealizedCurrentDataError);

  const hasURZOrRZFail =
    hasUnrealizedDataError ||
    hasRealizedPriorDataError ||
    hasRealizedCurrentDataError;

  const hasSuccessOrFailedCalls = hasBrokerageOrManagedAccount
    ? hasTaxAndTaxonomySuccessOrFail && hasRZAndRZSuccessOrFail
    : hasTaxAndTaxonomySuccessOrFail;

  const onCustomizeAccountViewLinkClick = (path) => {
    props.history.push(path)
  };

  if (hasSuccessOrFailedCalls) {
    return (
      <Fragment>
        <CTATarget messageName={''} messageText={''} messageId={''} />
        <div className={'taxes-page-title-v2'}>
          <PageHeader
            title="Taxes"
            className={'taxes-page-v2'}
            titleClassName={
              'b-color-text--set--white b-font-weight-bold b-typography-sans-serif--headline--600'
            }></PageHeader>
        </div>
        <div className="taxes-page-v2">
          <div className="taxes-page-print-v2">
            <div className="b-typography-sans-serif--body-loose--200">
              All data as of <FormattedDate>{asOfDate}</FormattedDate> unless
              otherwise noted.
            </div>
            {isDesktopLayout && (
              <AAMBeaconButton
                className="taxes-page-header-print-container"
                variant="tertiary"
                scale="normal"
                aria-label="print button"
                data-testid="beacon-print-button"
                onBeaconEventClick={() => {
                  handlePrint();
                }}>
                  Print
                  <PrintIcon
                    aria-label="lock"
                    className="print-icon"
                    slot="prefix"
                  />
              </AAMBeaconButton>
            )}
          </div>
          <div className="b-typography-sans-serif--body-loose--200">
            Hidden accounts are not included in the details below. To include
            these accounts, please visit the{' '}
            <span className={`e2e-tax-form-info-v2 beacon-on-light`} id="e2e-profile-customize-account-link">
              <AAMBeaconButton
                variant="tertiary"
                scale="normal"
                onBeaconEventClick={() => {
                  onCustomizeAccountViewLinkClick("/profile/customize-account-view");
                }}
              >
                Customize Account View
              </AAMBeaconButton>
            </span>{' '}
            page to unhide them.
          </div>
          <Fragment>
            <div className="taxes-page-header">
              {ownsBrokerage && (
                <BrokerageLinkWithPermissions
                  isMobileLayout={isMobileLayout}
                  shouldDisable
                />
              )}
            </div>

            <div className="tax-info-boxes">
              {taxSummaryEnabled() && <TaxSummaryV2 summary={taxSummary} />}
              {hasOwnedRetirementAccounts && (
                <IRASummaryV2 summary={iraSummary} />
              )}
              <TaxResourcesV2 isDesktopLayout={isDesktopLayout} />
            </div>
            <CostBasisTableV2
              hasCollegeSavingsOnly={false}
              tableData={costBasisTableData}
              unrealizedTableData={
                hasBrokerageOrManagedAccount ? unrealizedTableData : []
              }
              realizedData={
                hasBrokerageOrManagedAccount ? realizedTableData : {}
              }
              router={router}
              taxonomyDataV2={taxonomyDataLayout}
              isRetirementAdvisoryServiceClient={
                isRetirementAdvisoryServiceClient
              }
              hasOnlyViewOnlyPerishingAccounts={
                hasOnlyViewOnlyPerishingAccounts
              }
              hasURZOrRZFail={hasURZOrRZFail}
              pershingAccounts={pershingAccounts}
              printYear={printYear}
              setPrintYear={setPrintYear}
              preScrollFormatMobile={preScrollFormatMobile}
            />
            <YearToDateTableV2
              taxFilter={taxFilter}
              taxonomyDataV2={taxonomyData}
              isRetirementAdvisoryServiceClient={
                isRetirementAdvisoryServiceClient
              }
            />
          </Fragment>

          <LegalCopy content={Content.taxesDisclosureV2} />
        </div>
      </Fragment>
    );
  } else {
    return <PageLoadingSpinner name={'Taxes'} />;
  }
};

TaxesPage.propTypes = {
  costBasisTableData: object.isRequired,
  iraSummary: object.isRequired,
  isDesktopLayout: bool.isRequired,
  isMobileLayout: bool.isRequired,
  requestStatus: object.isRequired,
  hasBrokerageOrManagedAccount: bool,
  taxFilter: object.isRequired,
  taxSummary: object.isRequired,
  hasOwnedRetirementAccounts: bool.isRequired,
  ownsBrokerage: bool.isRequired
};

TaxesPage.defaultProps = {
  ownsBrokerage: false,
  hasOwnedCollegeSavings: false,
  hasOwnedRetirementAccounts: false
};

const mapStateToProps = (state) => ({
  costBasisTableData: getCostBasisTableData(state),
  asOfDate: getCostBasisAsOfDateV2(state),
  customerResidentialState: getCustomerResidentialState(state),
  iraSummary: getIRASummary(state),
  ownsBrokerage: hasOwnedBrokerage(state),
  hasBrokerageOrManagedAccount: hasBrokerageOrManagedAccounts(state),
  isDesktopLayout: isDesktopLayout(state),
  isMobileLayout: isMobileLayout(state),
  requestStatus: getRequestStatus(state),
  taxFilter: getTaxFilter(state),
  taxSummary: getTaxSummaryV2(state),
  failedTaxes: hasFailedRequestingTaxes(state),
  receivedTaxes: hasReceivedTaxes(state),
  taxonomyData: getTaxesTaxonomyData(state),
  taxonomyDataLayout: getTaxonomyDataV2(state),
  pershingAccounts: getPershingAccounts(state),
  hasTaxonomyDataError: getHasErrorTaxonomy(state),
  hasTaxonomyData: getHasReceivedTaxonomy(state),
  unrealizedTableData: getUnrealizedTableData(state),
  realizedTableData: getRealizedTableData(state),
  hasReceivedUnrealizedData: getHasReceivedUnrealizedData(state),
  hasReceivedRealizedPriorData: getHasReceivedRealizedPriorYearData(state),
  hasRealizedPriorDataError: getHasRealizedPriorYearDataError(state),
  hasReceivedRealizedCurrentData: getHasReceivedRealizedCurrentYearData(state),
  hasRealizedCurrentDataError: getHasRealizedCurrentYearDataError(state),
  hasUnrealizedDataError: getHasUnrealizedDataError(state),
  isRetirementAdvisoryServiceClient: isRetirementAdvisoryServiceClientSelector(state),
  hasOnlyViewOnlyPerishingAccounts: hasOnlyViewOnlyPerishingAccountsSelector(state),
  hasOwnedRetirementAccounts: hasOwnedRetirementAccountsSelector(state),
  router: getRouter(state),
  preScrollFormatMobile: getPreScrollFormatMobile(state)
});

export default withRouter(connect(mapStateToProps)(TaxesPage));
